import { HOME_SPOTS, HomeId, HomeSpot, LAST_RESORT_HOME } from "./rewardHomes";

/**
 * Where on screen a home is right now (rewardHomes.ts HOME_SPOTS lists its spots, most specific first). The rule
 * (owner 2026-10-24: "never fly off-screen"): the first spot that is laid out inside the viewport and not covered by a
 * sheet or a dialog; failing that, the first one inside the viewport at all (a dimmed button under a reward card still
 * shows where the reward went); failing that, the hub's tab bar (LAST_RESORT_HOME); with no hub at all, none, and the
 * gain waits for the hub (rewardFly.ts).
 */

export interface SpotState {
  /** Laid out, with its centre inside the viewport. */
  readonly inView: boolean;
  /** Something else is the topmost element at its centre (a sheet, a scrim). */
  readonly covered: boolean;
}

/** The index of the spot to fly to, or -1 for none. Pure: the DOM is read by `findHome`. */
export function chooseSpot(spots: readonly SpotState[]): number {
  const clear = spots.findIndex((s) => s.inView && !s.covered);
  return clear >= 0 ? clear : spots.findIndex((s) => s.inView);
}

/** A home found on screen: the element, its centre and the number inside it to tick (if it shows one). */
export interface HomeHit {
  readonly el: HTMLElement;
  readonly x: number;
  readonly y: number;
  readonly spot: HomeSpot;
  readonly counter: HTMLElement | null;
}

/** Whether `r`'s centre lies inside a `w` × `h` viewport and it has a size. */
export function rectInView(r: { readonly left: number; readonly top: number; readonly width: number; readonly height: number }, w: number, h: number): boolean {
  const cx = r.left + r.width / 2, cy = r.top + r.height / 2;
  return r.width > 0 && r.height > 0 && cx >= 0 && cy >= 0 && cx <= w && cy <= h;
}

/**
 * Reads the DOM for `home` (layout reads only — call it in the read phase, before any style is written this frame).
 * Every element a spot's selector matches is a candidate, in spot order then document order.
 */
export function findHome(home: HomeId, doc: Document = document): HomeHit | null {
  const w = innerWidth, h = innerHeight;
  const cands: { el: HTMLElement; spot: HomeSpot; r: DOMRect }[] = [];
  for (const spot of HOME_SPOTS[home]) for (const el of doc.querySelectorAll<HTMLElement>(spot.sel)) cands.push({ el, spot, r: el.getBoundingClientRect() });
  const states = cands.map(({ el, r }): SpotState => {
    const inView = rectInView(r, w, h);
    if (!inView) return { inView, covered: true };
    const top = doc.elementFromPoint(r.left + r.width / 2, r.top + r.height / 2);
    return { inView, covered: !!top && !el.contains(top) && !top.contains(el) };
  });
  const i = chooseSpot(states);
  if (i < 0) return home === LAST_RESORT_HOME ? null : findHome(LAST_RESORT_HOME, doc);
  const c = cands[i];
  return { el: c.el, x: c.r.left + c.r.width / 2, y: c.r.top + c.r.height / 2, spot: c.spot, counter: c.spot.counter ? c.el.querySelector<HTMLElement>(c.spot.counter) : null };
}

/** The live element for a spot found earlier (the hub re-renders its markup, so the one found at launch may be gone). */
export function liveSpot(hit: HomeHit, doc: Document = document): { el: HTMLElement; counter: HTMLElement | null } | null {
  const el = hit.el.isConnected ? hit.el : doc.querySelector<HTMLElement>(hit.spot.sel);
  if (!el) return null;
  return { el, counter: hit.spot.counter ? el.querySelector<HTMLElement>(hit.spot.counter) : null };
}
